import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems?: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
  showInfo?: boolean;
  itemLabel?: string;
}

/**
 * Reusable pagination component with ellipsis support
 *
 * Features:
 * - First/Last page navigation
 * - Previous/Next page navigation
 * - Smart ellipsis for large page counts
 * - Optional "Showing X-Y of Z" info text
 * - Loading/disabled state support
 */
export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  pageSize = 10,
  onPageChange,
  disabled = false,
  showInfo = true,
  itemLabel = 'itens',
}) => {
  if (totalPages <= 1) return null;

  // Calculate range for "Showing X-Y of Z" text
  const startItem = ((currentPage - 1) * pageSize) + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems || 0);

  // Generate page numbers with ellipsis
  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) pages.push(i);

      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }

    return pages;
  };

  const buttonBaseClass = "p-2 text-[#888888] hover:text-white hover:bg-[#333333] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
  const pageButtonClass = (isActive: boolean) => `
    w-10 h-10 rounded-lg font-medium transition-colors
    ${isActive
      ? 'bg-[#F4CC04] text-black'
      : 'text-[#888888] hover:text-white hover:bg-[#333333]'
    }
    disabled:opacity-50 disabled:cursor-not-allowed
  `;

  return (
    <div className="flex flex-col items-center gap-3">
      {/* Info text */}
      {showInfo && totalItems !== undefined && (
        <span className="text-sm text-[#888888]">
          Mostrando {startItem}-{endItem} de {totalItems} {itemLabel}
        </span>
      )}

      {/* Page indicator */}
      <span className="text-sm text-[#888888]">
        Página <span className="text-white font-medium">{currentPage}</span> de <span className="text-white font-medium">{totalPages}</span>
      </span>

      {/* Navigation buttons */}
      <div className="flex items-center gap-1">
        {/* First page */}
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1 || disabled}
          className={buttonBaseClass}
          title="Primeira página"
        >
          <ChevronsLeft className="w-5 h-5" />
        </button>

        {/* Previous page */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1 || disabled}
          className={buttonBaseClass}
          title="Página anterior"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        {/* Page numbers */}
        {getPageNumbers().map((page, idx) => (
          page === '...' ? (
            <span key={`ellipsis-${idx}`} className="px-2 text-[#888888]">...</span>
          ) : (
            <button
              key={page}
              onClick={() => onPageChange(page as number)}
              disabled={disabled}
              className={pageButtonClass(currentPage === page)}
            >
              {page}
            </button>
          )
        ))}

        {/* Next page */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || disabled}
          className={buttonBaseClass}
          title="Próxima página"
        >
          <ChevronRight className="w-5 h-5" />
        </button>

        {/* Last page */}
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages || disabled}
          className={buttonBaseClass}
          title="Última página"
        >
          <ChevronsRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default Pagination;
